import { Button, Col, Row } from "antd";
import Dragger from "antd/es/upload/Dragger";
import * as XLSX from "xlsx";
import csv from "../../assets/svg/icons/upload.svg";
import { useAlert } from "../../context/AlertProvider";
import { CloseOutlined, FileExcelOutlined } from "@ant-design/icons";

const backendFields = [
  "firstname",
  "lastname",
  "fullname",
  "emailId",
  "phoneNo",
  "companyName",
  "mobile",
  "oppurtunityAmount",
  "title",
  "website",
  "address",
  "industryText",
  "rate",
  "status",
  "leadSource",
  "description",
  "leadRating",
];

const properHeaderMapping = {
  firstname: "First Name",
  lastname: "Last Name",
  fullname: "Full Name",
  emailId: "Email",
  phoneNo: "Phone Number",
  companyName: "Company Name",
  mobile: "Mobile Number",
  oppurtunityAmount: "Opportunity Amount",
  title: "Designation",
  website: "Website",
  address: "Address",
  industryText: "Industry",
  rate: "Potential Revenue",
  status: "Status",
  leadSource: "Lead Source",
  description: "Description",
  leadRating: "Lead Rating",
};

// Auto-map headers function
const autoMapHeaders = (headers) => {
  const mapping = {};

  headers.forEach((header) => {
    // Exact match with properHeaderMapping
    const exactMatch = Object.entries(properHeaderMapping).find(
      ([field, properHeader]) =>
        properHeader.toLowerCase() === header.toLowerCase(),
    );
    if (exactMatch) {
      mapping[header] = exactMatch[0];
      return;
    }

    // Normalized match
    const normHeader = header.toLowerCase().replace(/[^a-z0-9]/g, "");
    const normMatch = backendFields.find(
      (field) => field.toLowerCase().replace(/[^a-z0-9]/g, "") === normHeader,
    );
    if (normMatch) {
      mapping[header] = normMatch;
      return;
    }

    // Partial match
    const partialMatch =
      backendFields.find((field) => normHeader.includes(field.toLowerCase())) ||
      backendFields.find((field) => field.toLowerCase().includes(normHeader));

    if (partialMatch) {
      mapping[header] = partialMatch;
      return;
    }

    mapping[header] = null; // fallback
  });

  return mapping;
};

// CSV header extraction
const getCSVHeaders = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const headers = text
        .split("\n")[0]
        .split(",")
        .map((h) => h.trim());
      resolve(headers);
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });

// XLSX header extraction
const getXLSXHeaders = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const range = XLSX.utils.decode_range(sheet["!ref"]);
      const firstRow = range.s.r;
      const headers = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = sheet[XLSX.utils.encode_cell({ r: firstRow, c })];
        headers.push(cell?.v || `Column-${c + 1}`);
      }
      resolve(headers);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

const CSVUpload = ({ csvImportPayload, setCsvImportPayload, setError }) => {
  const { showAlert } = useAlert();

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv,.xlsx",
    disabled: !!csvImportPayload,
    beforeUpload: async (file) => {
      setError();

      const formData = new FormData();
      formData.append("file", file);

      if (formData.get("file").size / 1024 / 1024 > 10) {
        setError(`${file.name} is larger than 10MB!`);
        showAlert("error", `${file.name} is larger than 10MB!`);
        return false;
      }

      let headers = [];
      if (file.name.endsWith(".csv")) headers = await getCSVHeaders(file);
      else if (file.name.endsWith(".xlsx"))
        headers = await getXLSXHeaders(file);

      const mappingItems = autoMapHeaders(headers);

      setCsvImportPayload({
        file: formData,
        fileHeaders: headers,
        mappingItems: Object.entries(mappingItems).map(([key, value]) => ({
          field: value,
          mappingField: key,
        })),
      });

      return false; // prevent default upload
    },
    fileList: csvImportPayload?.file
      ? [
          {
            uid: "-1",
            name: csvImportPayload.file.get("file").name,
            status: "done",
            originFileObj: csvImportPayload.file.get("file"),
          },
        ]
      : [],
  };

  return (
    <Row className="p-6">
      <Col span={24}>
        <div className="tw-mt-8 csv-upload">
          <Dragger {...uploadProps}>
            <div className="tw-py-5 tw-flex tw-flex-col tw-items-center tw-justify-center">
              {!csvImportPayload && (
                <>
                  <p className="tw-bg-secondary-color tw-rounded-[10px] tw-w-fit tw-p-4 tw-mb-2">
                    <img src={csv} alt="" className="tw-w-[27px]" />
                  </p>
                  <p className="tw-text-sm tw-font-semibold tw-mb-5">
                    Drag & drop your CSV file here
                  </p>
                  <div className="tw-text-xs tw-text-sub-text">
                    or click to browse
                  </div>
                  <Button
                    className="tw-my-3 !tw-bg-white tw-px-4 !tw-text-text-primary tw-shadow-none !tw-text-xs !tw-font-semibold tw-border tw-border-solid tw-border-border-secondary tw-rounded-[10px]"
                    type="primary"
                  >
                    Upload CSV
                  </Button>
                  <div className="tw-text-xs tw-text-sub-text">
                    Supports CSV & XLSX · Max 10MB
                  </div>
                </>
              )}
              {csvImportPayload && (
                <div className="tw-relative tw-bg-[#F6FFF2] tw-border-2 tw-border-dashed tw-border-[#2F9600] tw-rounded-[10px] tw-flex tw-items-center tw-justify-center tw-flex-col tw-py-10 tw-px-12">
                  <div
                    className="tw-flex tw-items-center tw-cursor-pointer tw-justify-center tw-absolute tw-bg-secondary-color tw-rounded-full tw-top-[-11px] tw-right-[-6px] tw-size-[20px]"
                    onClick={() => setCsvImportPayload()}
                  >
                    <CloseOutlined className="tw-text-xs" />
                  </div>
                  <p className="tw-bg-[#E0FFD2] tw-rounded-[10px] tw-w-fit tw-p-4 tw-mb-3">
                    <FileExcelOutlined
                      className="tw-text-xl"
                      style={{ color: "#2F9600" }}
                    />
                  </p>
                  <div className="tw-text-sm tw-font-semibold tw-text-text-primary">
                    {csvImportPayload?.file.get("file").name}
                  </div>
                  <div className="tw-text-xs tw-text-sub-text tw-pt-1">
                    {(csvImportPayload?.file.get("file").size / 1024).toFixed(
                      2,
                    )}{" "}
                    KB
                  </div>
                </div>
              )}
            </div>
          </Dragger>
        </div>
      </Col>
    </Row>
  );
};

export default CSVUpload;
